import { useState, useEffect } from 'react';

type UseSessionStorageForTrackParams = {
  trackId?: string | null;
  key: string;
  defaultValue?: any | null;
};

/**
 * Keeps data only while the tab is open.
 * If a user opens multiple tabs of the same application, each tab has its own separate sessionStorage.
 * Prevent Data Conflicts Across Tabs.
 * As soon as the user closes the tab, all stored data is cleared automatically.
 * Use case: Storing temporary UI state (e.g., form inputs, volume settings, or step progression in a checkout flow)
 * that should reset when the user starts a new session.
 */
const useSessionStorageForTrack = ({ trackId = null, key, defaultValue = null }: UseSessionStorageForTrackParams) => {
  const [value, setValue] = useState<any>(() => {
    return getJSONFromSessionStorage({ trackId, key, defaultValue });
  });

  useEffect(() => {
    setJSONInSessionStorage({ trackId, key, value });
  }, [key, trackId, value]);

  return [value, setValue];
};

const getJSONFromSessionStorage = ({ trackId = null, key, defaultValue = null }: UseSessionStorageForTrackParams) => {
  if (typeof window.sessionStorage === 'object') {
    const sessionStorageValue = sessionStorage.getItem(`htco${key}-${trackId}`);
    return sessionStorageValue ? JSON.parse(sessionStorageValue) : defaultValue;
  }
  return defaultValue;
};

const setJSONInSessionStorage = ({ trackId = null, key, value }: UseSessionStorageForTrackParams & { value: any }) => {
  sessionStorage.setItem(`htco${key}-${trackId}`, JSON.stringify(value));
};

export { getJSONFromSessionStorage, setJSONInSessionStorage, useSessionStorageForTrack };
